import { createReducer, on } from "@ngrx/store";
import { consentsPermissionsInitialState } from "../initial-states/consents-permissions.state";
import { loadUserConsentsPermissions, loadUserConsentsPermissionsFailed, loadUserConsentsPermissionsSuccess, revokeUserTenantConsent, updateApplicationInfo } from "../actions/consents-permissions.actions";
import { IConsentsPermissionsState, ITenantApplicationInfo, IUserTenantConsent, IUserTenantConsentStructure, PermissionsInfo, UserTenantConsentApplicationEnum } from "../../models/consents-permissions.model";

export const ConsentsPermissionsReducer = createReducer(consentsPermissionsInitialState,

	// The reducer runs when a dispatch 'loadUserConsentsPermissions' was triggered
	// The only thing we need to do here is to set the loading to true
	on(loadUserConsentsPermissions,
		(state) => ({
			...state,
			loading: true
		})
	),

	// The reducer runs when a dispatch 'loadUserConsentsPermissionsSuccess' was triggered
	// Here we need to set the loading to false, set the value of 'loadedOnce' and add the value of the tenants
	on(loadUserConsentsPermissionsSuccess,
		(state, { permissionsInfo }) => ({
			...state,
			loading: false,
			loadedOnce: true,
			value: permissionsInfo ? parseConsentsPermissions(permissionsInfo) : undefined
		})
	),

	// The reducer runs when a dispatch 'loadUserConsentsPermissionsFailed' was triggered
	// Here we need to set the state to the initial one, set the value of 'loadedOnce' and add the value of the error action result into the store
	on(loadUserConsentsPermissionsFailed,
		(state, { error }) => ({
			...state,
			loading: false,
			loadedOnce: false,
			error: {
				code: error.code,
				message: error.message,
				details: error.details,
				status: error.status,
				data: undefined
			}
		})
	),

	on(updateApplicationInfo, (state, { applicationsInfo }) => ({
		...state,
		value: updateConsentApplicationInfo(applicationsInfo, state.value),
	})),

	on(revokeUserTenantConsent, (state, { application, key }) => ({
		...state,
		value: state.value ? removeUserTenantConsent(state.value, application, key) : undefined,
	}))
)

function removeUserTenantConsent(state: IConsentsPermissionsState, application: UserTenantConsentApplicationEnum, key: string): IConsentsPermissionsState {
	const consents = { ...state.consents };
	consents[application] = consents[application]?.filter(consent => consent.key !== key);
	return {
		...state,
		consents
	};
}

/** Convert Consent Tenant in bulk */
function parseConsentsPermissions(permissionsInfo: PermissionsInfo): IConsentsPermissionsState {
	const consents = permissionsInfo.permissions;

	// Parse each consent tenant
	consents.forEach(consent => { parseConsentTenant(consent); });

	// Order by expire date descending
	consents.sort((a, b) => new Date(b.expireDate).getTime() - new Date(a.expireDate).getTime());

	const parsedStructure: IUserTenantConsentStructure = {};
	Object.keys(UserTenantConsentApplicationEnum).forEach(key => {
		const enumKey = UserTenantConsentApplicationEnum[key as keyof typeof UserTenantConsentApplicationEnum];
		parsedStructure[enumKey] = consents.filter(consent => consent.internalName === enumKey) ?? [];
	});

	return {
		consents: parsedStructure,
		consentedApplications: permissionsInfo.consentedApplications
	};
}

/** Convert Consent Tenant by parsing some data */
function parseConsentTenant(consent: IUserTenantConsent): IUserTenantConsent {

	// Parse identity letters
	consent.identity.letters = consent.identity ? consent.identity.name ? consent.identity.name?.substring(0, 2)?.toUpperCase() : consent.identity.email?.substring(0, 2)?.toUpperCase() : "UN";
	consent.identity.photoUrl = consent.identity?.photoUrl ? `data:image;base64,${consent.identity?.photoUrl}` : undefined;

	return consent;
}

function updateConsentApplicationInfo(applicationsInfo: ITenantApplicationInfo[], state?: IConsentsPermissionsState | undefined): IConsentsPermissionsState | undefined {

	if (!state) { return state; }

	const consents = { ...state.consents };
	Object.keys(consents).forEach(key => {
		const consentArray = consents[key as keyof typeof consents];
		const consent = consentArray?.[0];
		if (consent) {
			const applicationInfoIndex = applicationsInfo.findIndex(app => app.app.id === consent.internalName);
			if (applicationInfoIndex != -1) { consent.applicationInfo = applicationsInfo[applicationInfoIndex]; }
		}
	});

	return {
		...state,
		consents
	};
}